import React, { useState, useEffect, useCallback } from 'react';
import AdminNavbar from '../Navbars/AdminNavbar';
import AdminSidebar from '../Sidebar/AdminSidebar';
import { SuperAdminApis } from '../../apis/superAdminApi';
import { AxiosResponse } from 'axios';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { Oval } from 'react-loader-spinner';
//@ts-ignore
import Modal from 'react-awesome-modal';
import { SvgElement, icontypesEnum } from '../assets/svgElement';
import PayoutProviderSwitch from './PayoutProviderSwitch';

function AdminWithdrawals() {
    const [withdrawals, setWithdrawals] = useState<any>([]);
    const [summary, setSummary] = useState<any>(null);
    const [loader, setLoader] = useState(false);
    const [actionLoader, setActionLoader] = useState(false);

    // Filters
    const [search, setSearch] = useState('');
    const [status, setStatus] = useState('all');
    const [paymentType, setPaymentType] = useState('all');
    const [dateFrom, setDateFrom] = useState('');
    const [dateTo, setDateTo] = useState('');

    // Modals
    const [retryModal, setRetryModal] = useState(false);
    const [retryId, setRetryId] = useState<number | null>(null);
    const [retryData, setRetryData] = useState<any>(null);

    const [completeModal, setCompleteModal] = useState(false);
    const [completeId, setCompleteId] = useState<number | null>(null);
    const [completeData, setCompleteData] = useState<any>(null);
    const [completeNotes, setCompleteNotes] = useState('');

    const [detailModal, setDetailModal] = useState(false);
    const [selectedWithdrawal, setSelectedWithdrawal] = useState<any>(null);

    // ── Load withdrawals ───────────────────────────────────────
    const loadWithdrawals = useCallback((page?: string) => {
        setLoader(true);
        const params: any = {
            search, status: status !== 'all' ? status : undefined,
            payment_type: paymentType !== 'all' ? paymentType : undefined,
            date_from: dateFrom || undefined, date_to: dateTo || undefined,
            page: page || '1', per_page: '20',
        };

        SuperAdminApis.getAdminWithdrawals(params).then(
            (response: AxiosResponse<any>) => {
                setLoader(false);
                if (response?.data?.status) {
                    setWithdrawals(response.data.data);
                    setSummary(response.data.summary);
                }
            }
        ).catch(() => { setLoader(false); toast.error('Failed to load withdrawals.'); });
    }, [search, status, paymentType, dateFrom, dateTo]);

    useEffect(() => { loadWithdrawals(); }, []);

    // ── Check status with the provider ────────────────────────
    // For a withdrawal whose webhook never arrived: the provider that sent it
    // is asked directly, and a failure returns the money to the wallet.
    const [syncingId, setSyncingId] = useState<number | null>(null);

    const handleSync = (w: any) => {
        setSyncingId(w.id);
        SuperAdminApis.syncWithdrawal(w.id).then((res: AxiosResponse<any>) => {
            const d = res?.data;
            if (d?.status) {
                const outcome = d.data?.outcome;
                (outcome === 'success' ? toast.success : outcome === 'pending' ? toast.info : toast.warn)(d.message, { autoClose: 10000 });
                loadWithdrawals();
            } else {
                toast.error(d?.message || 'Could not check the status.');
            }
        }).catch((err: any) => {
            toast.error(err?.response?.data?.message || 'Could not check the status.');
        }).finally(() => setSyncingId(null));
    };

    // ── Retry failed withdrawal ────────────────────────────────
    const handleRetry = () => {
        if (!retryId) return;
        setActionLoader(true);
        SuperAdminApis.retryWithdrawal(retryId).then((res: AxiosResponse<any>) => {
            setActionLoader(false);
            setRetryModal(false);
            if (res?.data?.status) { toast.success(res.data.message); loadWithdrawals(); }
            else { toast.error(res?.data?.message || 'Retry failed.'); }
        }).catch((err: any) => {
            setActionLoader(false);
            toast.error(err?.response?.data?.message || 'Retry failed.');
        });
    };

    // ── Mark as completed ──────────────────────────────────────
    const handleMarkComplete = () => {
        if (!completeId) return;
        setActionLoader(true);
        SuperAdminApis.markWithdrawalComplete(completeId, { notes: completeNotes }).then((res: AxiosResponse<any>) => {
            setActionLoader(false);
            setCompleteModal(false);
            setCompleteNotes('');
            if (res?.data?.status) { toast.success(res.data.message); loadWithdrawals(); }
            else { toast.error(res?.data?.message || 'Failed.'); }
        }).catch((err: any) => {
            setActionLoader(false);
            toast.error(err?.response?.data?.message || 'Failed.');
        });
    };

    // ── View details ───────────────────────────────────────────
    const viewDetails = (w: any) => {
        setSelectedWithdrawal(w);
        setDetailModal(true);
    };

    // ── Paginator ──────────────────────────────────────────────
    const paginate = (label: string) => {
        const page = label === 'Next &raquo;' || label === '&laquo; Previous'
            ? withdrawals?.links?.find((l: any) => l.label === label)?.url?.split('page=')[1]
            : label;
        if (page) loadWithdrawals(page);
    };

    const formatAmount = (amount: any) => '₦' + Number(amount || 0).toLocaleString('en-NG', { minimumFractionDigits: 2 });

    const statusBadge = (s: string) => {
        switch (s) {
            case 'completed': return 'bg-green-100 text-green-700';
            case 'processing': return 'bg-blue-100 text-blue-700';
            case 'pending': return 'bg-yellow-100 text-yellow-700';
            case 'failed': return 'bg-red-100 text-red-700';
            default: return 'bg-gray-100 text-gray-700';
        }
    };

    return (
        <>
            <AdminSidebar title="Withdrawals" />
            <div className="relative md:ml-64 bg-[#FAFAFA]">
                <AdminNavbar title="Withdrawals" />

                <div className="lg:px-10 pb-6 px-6 bg-[#FAFAFA] min-h-screen">
                    <h3 className="text-[15px] text-[#1C1C1C] font-semibold mt-5 mb-2">Withdrawal Management</h3>
                    <p className="text-xs text-gray-500 mb-4">View all rider and fleet agent withdrawals. Retry failed transfers or manually mark as completed.</p>

                    {/* Which rail sends the money. Above the numbers because it is
                        the one control on this page that changes what happens next. */}
                    <PayoutProviderSwitch />

                    {/* ── Summary Cards ─────────────────────────────── */}
                    {summary && (
                        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-5">
                            <div className="bg-white rounded-lg p-4 shadow-sm">
                                <p className="text-xs text-gray-500">Completed</p>
                                <p className="text-xl font-bold text-green-600">{summary.completed_count}</p>
                                <p className="text-xs text-gray-400">{formatAmount(summary.completed_amount)}</p>
                            </div>
                            <div className="bg-white rounded-lg p-4 shadow-sm">
                                <p className="text-xs text-gray-500">Processing / Pending</p>
                                <p className="text-xl font-bold text-blue-600">{summary.pending_count}</p>
                                <p className="text-xs text-gray-400">{formatAmount(summary.pending_amount)}</p>
                            </div>
                            <div className="bg-white rounded-lg p-4 shadow-sm">
                                <p className="text-xs text-gray-500">Failed</p>
                                <p className="text-xl font-bold text-red-600">{summary.failed_count}</p>
                                <p className="text-xs text-gray-400">{formatAmount(summary.failed_amount)}</p>
                            </div>
                            <div className="bg-white rounded-lg p-4 shadow-sm">
                                <p className="text-xs text-gray-500">Total Paid Out</p>
                                <p className="text-xl font-bold text-gray-800">{formatAmount(summary.total_amount)}</p>
                                <p className="text-xs text-gray-400">{summary.total_count} total withdrawals</p>
                            </div>
                        </div>
                    )}

                    {/* ── Filters ───────────────────────────────────── */}
                    <div className="bg-white rounded-lg p-4 shadow-sm mb-4">
                        <div className="flex flex-wrap gap-3 items-end">
                            <div className="flex-1 min-w-[150px]">
                                <label className="text-xs text-gray-500">Search</label>
                                <input type="text" value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Name, email, reference, bank..." className="w-full border border-gray-300 rounded-md p-2 text-sm" />
                            </div>
                            <div className="min-w-[120px]">
                                <label className="text-xs text-gray-500">Status</label>
                                <select value={status} onChange={(e) => setStatus(e.target.value)} className="w-full border border-gray-300 rounded-md p-2 text-sm">
                                    <option value="all">All</option>
                                    <option value="pending">Pending</option>
                                    <option value="processing">Processing</option>
                                    <option value="completed">Completed</option>
                                    <option value="failed">Failed</option>
                                </select>
                            </div>
                            <div className="min-w-[120px]">
                                <label className="text-xs text-gray-500">Type</label>
                                <select value={paymentType} onChange={(e) => setPaymentType(e.target.value)} className="w-full border border-gray-300 rounded-md p-2 text-sm">
                                    <option value="all">All</option>
                                    <option value="fleet">Fleet Agent</option>
                                    <option value="independent">Independent Rider</option>
                                </select>
                            </div>
                            <div className="min-w-[130px]">
                                <label className="text-xs text-gray-500">From</label>
                                <input type="date" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)} className="w-full border border-gray-300 rounded-md p-2 text-sm" />
                            </div>
                            <div className="min-w-[130px]">
                                <label className="text-xs text-gray-500">To</label>
                                <input type="date" value={dateTo} onChange={(e) => setDateTo(e.target.value)} className="w-full border border-gray-300 rounded-md p-2 text-sm" />
                            </div>
                            <button onClick={() => loadWithdrawals()} className="bg-[#E04343] text-white px-4 py-2 rounded-md text-sm font-semibold">Filter</button>
                        </div>
                    </div>

                    {/* ── Table ─────────────────────────────────────── */}
                    <div className="relative overflow-x-auto">
                        <table className="w-full text-sm text-left text-gray-500 border-separate border-spacing-y-2">
                            <thead className="text-[11px] text-[#030229] bg-[#FAFAFA] uppercase">
                                <tr>
                                    <th className="px-2 py-2">Rider / Agent</th>
                                    <th className="px-2 py-2">Reference</th>
                                    <th className="px-2 py-2">Amount</th>
                                    <th className="px-2 py-2">Fee</th>
                                    <th className="px-2 py-2">Net</th>
                                    <th className="px-2 py-2">Bank</th>
                                    <th className="px-2 py-2">Status</th>
                                    <th className="px-2 py-2">Date</th>
                                    <th className="px-4 py-2">Action</th>
                                </tr>
                            </thead>
                            <tbody>
                                {withdrawals?.data?.length > 0 ? withdrawals.data.map((w: any) => (
                                    <tr key={w.id} className="bg-white">
                                        <td className="px-2 py-3">
                                            <div className="text-xs font-semibold">{w.user?.first_name} {w.user?.last_name}</div>
                                            <div className="text-[10px] text-gray-400">{w.user?.email}</div>
                                            {w.user?.rider_profile?.registration_type && (
                                                <span className={`text-[9px] px-1 py-0.5 rounded font-bold ${w.user?.rider_profile?.registration_type === 'fleet_agent' ? 'bg-purple-100 text-purple-700' : 'bg-blue-100 text-blue-700'}`}>
                                                    {w.user?.rider_profile?.registration_type === 'fleet_agent' ? 'Fleet Agent' : 'Rider'}
                                                </span>
                                            )}
                                        </td>
                                        <td className="px-2 py-3 text-xs font-mono">
                                            {w.reference}
                                            {w.provider && (
                                                <span className={`block mt-0.5 font-sans text-[9px] px-1 py-0.5 rounded w-max ${
                                                    w.provider === 'flutterwave' ? 'bg-orange-100 text-orange-700' : 'bg-blue-100 text-blue-700'}`}>
                                                    {w.provider}
                                                </span>
                                            )}
                                        </td>
                                        <td className="px-2 py-3 text-xs font-semibold">{formatAmount(w.amount)}</td>
                                        <td className="px-2 py-3 text-xs text-gray-400">{formatAmount(w.fee)}</td>
                                        <td className="px-2 py-3 text-xs font-semibold">{formatAmount(w.net_amount)}</td>
                                        <td className="px-2 py-3">
                                            <div className="text-xs">{w.bank_name}</div>
                                            <div className="text-[10px] text-gray-400">{w.account_number} • {w.account_name}</div>
                                        </td>
                                        <td className="px-2 py-3">
                                            <span className={`text-[10px] px-1.5 py-0.5 rounded font-bold ${statusBadge(w.status)}`}>
                                                {w.status}
                                            </span>
                                            {w.failure_reason && (
                                                <p className="text-[9px] text-red-500 mt-0.5 max-w-[120px] truncate" title={w.failure_reason}>
                                                    {w.failure_reason}
                                                </p>
                                            )}
                                        </td>
                                        <td className="px-2 py-3 text-xs">
                                            {w.created_at ? new Date(w.created_at).toLocaleDateString() : '—'}
                                            <div className="text-[10px] text-gray-400">
                                                {w.created_at ? new Date(w.created_at).toLocaleTimeString() : ''}
                                            </div>
                                        </td>
                                        <td className="px-4 py-3">
                                            <div className="flex flex-col gap-1">
                                                <button onClick={() => viewDetails(w)} className="text-blue-600 hover:underline text-xs font-semibold">View</button>
                                                {(w.status === 'pending' || w.status === 'processing') && (
                                                    <button onClick={() => handleSync(w)} disabled={syncingId === w.id}
                                                        title={`Ask ${w.provider || 'the provider'} where this transfer stands`}
                                                        className="text-purple-600 hover:underline text-xs font-semibold disabled:opacity-50">
                                                        {syncingId === w.id ? 'Checking…' : 'Check status'}
                                                    </button>
                                                )}
                                                {w.status === 'failed' && (
                                                    <button onClick={() => { setRetryId(w.id); setRetryData(w); setRetryModal(true); }} className="text-orange-600 hover:underline text-xs font-semibold">Retry</button>
                                                )}
                                                {(w.status === 'pending' || w.status === 'processing' || w.status === 'failed') && (
                                                    <button onClick={() => { setCompleteId(w.id); setCompleteData(w); setCompleteModal(true); }} className="text-green-600 hover:underline text-xs font-semibold">Complete</button>
                                                )}
                                            </div>
                                        </td>
                                    </tr>
                                )) : (
                                    <tr><td colSpan={9} className="text-center py-8 text-gray-400">No withdrawals found.</td></tr>
                                )}
                            </tbody>
                        </table>
                    </div>

                    {/* ── Pagination ────────────────────────────────── */}
                    <div className="flex justify-end mt-4">
                        {withdrawals?.links?.map((link: any, idx: number) => (
                            <button key={idx} onClick={() => paginate(link.label)} disabled={link.active || !link.url}
                                className={`mx-0.5 py-1 px-2 text-xs ${link.active ? 'bg-[#E04343] text-white' : link.url ? 'bg-gray-200 text-black' : 'bg-gray-100 text-gray-400'}`}>
                                {link.label.replace('&laquo; Previous', '< Prev').replace('Next &raquo;', 'Next >')}
                            </button>
                        ))}
                    </div>
                </div>
            </div>

            {/* ── Retry Modal ──────────────────────────────────── */}
            <Modal visible={retryModal} width="420" height="220" effect="fadeInUp" onClickAway={() => setRetryModal(false)}>
                <div className="p-4">
                    <div className="flex justify-between mb-3">
                        <p className="font-bold">Retry Failed Withdrawal</p>
                        <p className="cursor-pointer" onClick={() => setRetryModal(false)}><SvgElement type={icontypesEnum.CANCEL} /></p>
                    </div>
                    {retryData && (
                        <div className="text-sm text-gray-600 mb-4">
                            <p>Rider: <strong>{retryData.user?.first_name} {retryData.user?.last_name}</strong></p>
                            <p>Amount: <strong>{formatAmount(retryData.amount)}</strong> → {retryData.bank_name} ({retryData.account_number})</p>
                            <p className="text-xs text-red-500 mt-1">Failed: {retryData.failure_reason || 'Unknown reason'}</p>
                            <p className="text-xs text-gray-400 mt-2">This will debit the wallet again and re-initiate the Paystack transfer.</p>
                        </div>
                    )}
                    <button onClick={handleRetry} disabled={actionLoader} className="bg-orange-600 text-white py-2 rounded w-full text-sm font-semibold flex items-center justify-center gap-2">
                        Retry Transfer
                        <Oval visible={actionLoader} height="16" width="16" color="#fff" secondaryColor="#ccc" ariaLabel="loading" />
                    </button>
                </div>
            </Modal>

            {/* ── Mark Complete Modal ───────────────────────────── */}
            <Modal visible={completeModal} width="420" height="280" effect="fadeInUp" onClickAway={() => setCompleteModal(false)}>
                <div className="p-4">
                    <div className="flex justify-between mb-3">
                        <p className="font-bold">Mark as Completed</p>
                        <p className="cursor-pointer" onClick={() => setCompleteModal(false)}><SvgElement type={icontypesEnum.CANCEL} /></p>
                    </div>
                    {completeData && (
                        <div className="text-sm text-gray-600 mb-3">
                            <p>Rider: <strong>{completeData.user?.first_name} {completeData.user?.last_name}</strong></p>
                            <p>Amount: <strong>{formatAmount(completeData.amount)}</strong> → {completeData.bank_name}</p>
                            <p className="text-xs text-gray-400 mt-1">Use this if the payment was made manually or the Paystack webhook was missed.</p>
                        </div>
                    )}
                    <div className="mb-3">
                        <label className="text-xs text-gray-600 mb-1 block">Notes (optional)</label>
                        <textarea value={completeNotes} onChange={(e) => setCompleteNotes(e.target.value)} className="w-full border border-gray-300 rounded-md p-2 text-sm" rows={2} placeholder="e.g. Paid via manual bank transfer" />
                    </div>
                    <button onClick={handleMarkComplete} disabled={actionLoader} className="bg-green-600 text-white py-2 rounded w-full text-sm font-semibold flex items-center justify-center gap-2">
                        Mark as Completed
                        <Oval visible={actionLoader} height="16" width="16" color="#fff" secondaryColor="#ccc" ariaLabel="loading" />
                    </button>
                </div>
            </Modal>

            {/* ── Detail Modal ──────────────────────────────────── */}
            <Modal visible={detailModal} width="500" height="450" effect="fadeInUp" onClickAway={() => setDetailModal(false)}>
                <div className="p-4 overflow-y-auto max-h-[440px]">
                    <div className="flex justify-between mb-3">
                        <p className="font-bold">Withdrawal Details</p>
                        <p className="cursor-pointer" onClick={() => setDetailModal(false)}><SvgElement type={icontypesEnum.CANCEL} /></p>
                    </div>
                    {selectedWithdrawal && (
                        <div className="space-y-3 text-sm">
                            <div className="grid grid-cols-2 gap-3">
                                <div>
                                    <p className="text-[10px] text-gray-400 uppercase">Rider</p>
                                    <p className="text-xs font-semibold">{selectedWithdrawal.user?.first_name} {selectedWithdrawal.user?.last_name}</p>
                                    <p className="text-[10px] text-gray-400">{selectedWithdrawal.user?.email}</p>
                                </div>
                                <div>
                                    <p className="text-[10px] text-gray-400 uppercase">Reference</p>
                                    <p className="text-xs font-mono">{selectedWithdrawal.reference}</p>
                                </div>
                                <div>
                                    <p className="text-[10px] text-gray-400 uppercase">Amount</p>
                                    <p className="text-xs font-semibold">{formatAmount(selectedWithdrawal.amount)}</p>
                                </div>
                                <div>
                                    <p className="text-[10px] text-gray-400 uppercase">Fee / Net</p>
                                    <p className="text-xs">{formatAmount(selectedWithdrawal.fee)} / {formatAmount(selectedWithdrawal.net_amount)}</p>
                                </div>
                                <div>
                                    <p className="text-[10px] text-gray-400 uppercase">Bank</p>
                                    <p className="text-xs">{selectedWithdrawal.bank_name}</p>
                                    <p className="text-[10px] text-gray-400">{selectedWithdrawal.account_number} • {selectedWithdrawal.account_name}</p>
                                </div>
                                <div>
                                    <p className="text-[10px] text-gray-400 uppercase">Status</p>
                                    <span className={`text-[10px] px-1.5 py-0.5 rounded font-bold ${statusBadge(selectedWithdrawal.status)}`}>
                                        {selectedWithdrawal.status}
                                    </span>
                                </div>
                                <div>
                                    <p className="text-[10px] text-gray-400 uppercase">Requested</p>
                                    <p className="text-xs">{selectedWithdrawal.created_at ? new Date(selectedWithdrawal.created_at).toLocaleString() : '—'}</p>
                                </div>
                                <div>
                                    <p className="text-[10px] text-gray-400 uppercase">Processed</p>
                                    <p className="text-xs">{selectedWithdrawal.processed_at ? new Date(selectedWithdrawal.processed_at).toLocaleString() : '—'}</p>
                                </div>
                                {selectedWithdrawal.completed_at && (
                                    <div>
                                        <p className="text-[10px] text-gray-400 uppercase">Completed</p>
                                        <p className="text-xs">{new Date(selectedWithdrawal.completed_at).toLocaleString()}</p>
                                    </div>
                                )}
                                {selectedWithdrawal.failed_at && (
                                    <div>
                                        <p className="text-[10px] text-gray-400 uppercase">Failed</p>
                                        <p className="text-xs text-red-600">{new Date(selectedWithdrawal.failed_at).toLocaleString()}</p>
                                    </div>
                                )}
                            </div>
                            {selectedWithdrawal.failure_reason && (
                                <div className="bg-red-50 border border-red-200 rounded-lg p-3">
                                    <p className="text-[10px] text-red-500 uppercase font-semibold">Failure Reason</p>
                                    <p className="text-xs text-red-700">{selectedWithdrawal.failure_reason}</p>
                                </div>
                            )}
                            {selectedWithdrawal.paystack_transfer_code && (
                                <div className="bg-gray-50 rounded-lg p-3">
                                    <p className="text-[10px] text-gray-400 uppercase font-semibold">Paystack Details</p>
                                    <p className="text-xs">Transfer Code: {selectedWithdrawal.paystack_transfer_code}</p>
                                    <p className="text-xs">Paystack Ref: {selectedWithdrawal.paystack_reference}</p>
                                    <p className="text-xs">Paystack Status: {selectedWithdrawal.paystack_status}</p>
                                </div>
                            )}
                        </div>
                    )}
                </div>
            </Modal>

            <ToastContainer position="top-right" autoClose={2000} hideProgressBar />
        </>
    );
}

export default AdminWithdrawals;